import { COLOR_PALETTE, CHART_COLORS, CHART_STYLES } from './chart.constants';

const formatNumber = (value) => (
  typeof value === 'number'
    ? value.toLocaleString(undefined, { maximumFractionDigits: 2 })
    : value
);

export const formatPercent = (value) => `${Math.round((value ?? 0) * 100)}%`;

export const seriesColor = (item, index) => item.color ?? COLOR_PALETTE[index % COLOR_PALETTE.length];

export const chartFormatter = ({
  name, value, color, percent
}) => {
  const percentValue = percent ? ` (${Math.round(percent)}%)` : '';
  return `<span style="color:${color}">●</span> ${name} : <span style="color:${color}">${formatNumber(value)}</span> ${percentValue}`;
};

const tooltipBase = {
  appendToBody: true,
  backgroundColor: CHART_STYLES.BACKGROUND_COLOR,
  borderColor: CHART_STYLES.BORDER_COLOR,
  borderWidth: 1,
  padding: [8, 12],
  textStyle: {
    color: CHART_STYLES.TEXT_COLOR,
    fontSize: 13,
  },
  extraCssText: 'border-radius: 8px; box-shadow: 0 2px 10px rgba(15, 23, 42, 0.08);',
};

const axisTooltip = (params = []) => {
  const title = params[0]?.axisValueLabel ?? '';
  const rows = params.map((item) => {
    const pointValue = item.data?.value ?? item.value;
    const display = item.data?.raw === undefined
      ? formatNumber(pointValue)
      : formatPercent(pointValue);
    return chartFormatter({ name: item.seriesName, value: display, color: item.color });
  });
  return [title, ...rows].join('<br/>');
};

export const getTooltip = (overrides = {}) => ({
  ...tooltipBase,
  trigger: 'axis',
  formatter: axisTooltip,
  ...overrides,
});

export const getLegend = (overrides = {}) => ({
  type: 'scroll',
  top: 0,
  right: 0,
  icon: 'circle',
  itemWidth: 8,
  itemHeight: 8,
  itemGap: 16,
  textStyle: { color: CHART_STYLES.TEXT_COLOR, fontSize: 12 },
  pageIconColor: CHART_COLORS.BLUE,
  pageIconInactiveColor: 'lightgray',
  pageTextStyle: { color: CHART_STYLES.LABEL_COLOR },
  ...overrides,
});

export const getCategoryAxis = (categories, boundaryGap) => ({
  type: 'category',
  data: categories,
  boundaryGap,
  axisLine: { show: false },
  axisTick: { show: false },
  axisLabel: {
    color: CHART_STYLES.LABEL_COLOR,
    fontSize: 12,
    margin: 14,
    hideOverlap: true,
  },
});

export const getValueAxis = (overrides = {}) => ({
  type: 'value',
  min: 0,
  axisLine: { show: false },
  axisTick: { show: false },
  axisLabel: {
    color: CHART_STYLES.LABEL_COLOR,
    fontSize: 12,
    formatter: formatNumber,
  },
  splitLine: {
    lineStyle: { color: CHART_STYLES.SPLIT_LINE_COLOR },
  },
  ...overrides,
});

export const lineDash = (item) => (item.dashed ? { type: 'dashed' } : {});

// Each bucket is drawn as a share of that bucket's total so the stack fills 100%.
export const toRelativeSeries = (series, categoryCount) => {
  const totals = [];
  for (let bucketIndex = 0; bucketIndex < categoryCount; bucketIndex += 1) {
    let total = 0;
    series.forEach((item) => {
      total += item.values?.[bucketIndex] ?? 0;
    });
    totals.push(total);
  }

  return series.map((item) => {
    const points = [];
    for (let bucketIndex = 0; bucketIndex < categoryCount; bucketIndex += 1) {
      const raw = item.values?.[bucketIndex] ?? 0;
      const total = totals[bucketIndex];
      points.push({
        value: total ? raw / total : 0,
        raw,
      });
    }
    return { ...item, points };
  });
};